<template>
	<ErrorsErrorBoundary>
		<NuxtLayout>
			<ProductSeatSelector
				v-if="!!seatsIOConfig"
				:seats-io-workspace-key="seatsIOConfig.PUBLIC_WORKSPACE_KEY"
				:seats-io-event="seatsIOConfig.EVENT_ID" />

			<ErrorsErrorPopup />
			<ModalLoadingPopup />
			<CheckoutConfirmationPopup />

			<NuxtPage />

			<CookiesNoticePopup />
			<CartTimeoutExpiredPopup />
			<CartTimeoutWarningPopup />
		</NuxtLayout>
	</ErrorsErrorBoundary>
</template>

<script setup>
	import { SHOW_ERROR } from "assets/events/errors.js"

	//Note that all pages will use the default layout. Each page may utilize an additional layout nested within the default layout.
	//The Showing/Hiding logic of the Header and Footer is contained within the Header/Footer components.
	const {
		$persistentStores,
		$cartService,
		$currency,
		$cartTimeout,
		$messages,
		$productsService,
		$login,
		$seatSelector,
		$eventBus,
	} = useNuxtApp()

	const { t } = useI18n()
	$messages.$init(t)
	$currency.$init()

	const { $configs } = useNuxtApp()
	const runtimeConfig = useRuntimeConfig()
	const appConfig = useAppConfig()

	//detectTranslator: Shows a warning popup if the user is using a browser translator
	let translationObserver = ref(null)
	const detectTranslator = () => {
		const showWarningIfNotEnglish = (currentLang) => {
			if (currentLang.indexOf("en") <= 0) {
				$eventBus.$emit(SHOW_ERROR, {
					titleText: `${$messages.$t("detectedTranslatorTitle")}`,
					bodyText: `${$messages.$t("detectedTranslatorBody")}`,
				})
			}
		}

		const currentLang = document.documentElement.lang
		showWarningIfNotEnglish(currentLang)

		const observer = new MutationObserver(() => {
			const newLang = document.documentElement.lang
			showWarningIfNotEnglish(newLang)
		})

		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["lang"],
		})
	}

	const showMaintenancePage = runtimeConfig.public.SHOW_MAINTENANCE_PAGE
	const shouldShowMaintenancePage = $configs.$isConfigEnabled(showMaintenancePage)
	if (!shouldShowMaintenancePage && !appConfig.showMaintenancePage) {
		$persistentStores.$init()

		$productsService.$getAppSettings()

		onMounted(() => {
			//Make sure $cartTimeout.$init() is called before persistentStores.$refresh(), so that the watcher can detect the change in expiresAt
			$cartTimeout.$init()
			$cartService.$init()
			$persistentStores.$refresh()
			$login.$init()
			$seatSelector.$init()

			refreshStoresOnTabChange()
			detectTranslator()
		})

		onUnmounted(() => {
			if (translationObserver.value) translationObserver.value.disconnect()
		})

		const refreshStoresOnTabChange = () => {
			//Reload the stores if the tab changes to prevent users from opening multiple tabs to browse the site.
			document.addEventListener("visibilitychange", () => {
				if (document.visibilityState === "visible") {
					$persistentStores.$refresh()
				}
			})
		}

		//The preload is in place to prevent the "error dynamically loading" javascript error
		//This is needed since the confirmation page is dynamically navigated to from the checkout page
		preloadRouteComponents(["/", "/order/confirmation"])
	}

	// Load Seats Reservation Popup as a global component. Opted for this design so that the use can view/edit their selection from any
	// page (no need to redirect back to lodging). However, this architecture only allows for one reservation chart sitewide instead of per product.
	const seatsIOConfig = runtimeConfig.public.SEATS_IO
</script>
